import React from 'react';
import {
  AuthInputTextField,
  AuthSeparator,
  EmailTagInput,
  TextButton,
  Tag,
} from '@client/components';
import RadioSelect from '../RadioSelectOptions';
import { OAuthConnection } from '@client/components/OauthConnection';
import { AdvisorField } from './AdvisorField';
import { Bell, BellOff } from 'lucide-react';

interface CommunicationTabProps {
  advisorState: AdvisorProfile;
  errors: Partial<Record<keyof AdvisorProfile, boolean>>;
  maxSchedulingLinkDisplayTextLength: number;
  handleAdvisorStateChange: (
    advisorKey: keyof AdvisorProfile,
  ) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleShouldSendClientEmailChange: (value: string | number) => void;
  emailTags: Tag[];
  handleEmailTagsChange: (emails: Tag[]) => void;
  setHasUnsavedChanges: (hasChanges: boolean) => void;
  setAdvisorState: React.Dispatch<React.SetStateAction<AdvisorProfile>>;
  clearEmailWarnings: boolean;
  handleNotificationPreferenceChange: (value: NotificationPreference) => void;
}

export const shouldSendClientEmailRadioOptions = [
  {
    label: 'Manual Sharing:',
    description:
      "You'll manually choose when and how to share results with each client. (best for clients who may get intimidated or confused without your help)",
    value: 'no',
    id: 'no-client-results',
  },
  {
    label: 'Enable Client Self-Service:',
    description:
      'Give new client(s) direct access to view their results upon invite. (best for clients who are tech-savvy self starters)',
    value: 'yes',
    id: 'yes-client-results',
  },
];

export const notificationOptions = [
  {
    label: 'Allow Notifications',
    description: 'Receive updates and alerts about new opportunities as they become available.',
    value: 'allow',
    id: 'allow-notifications',
    icon: <Bell className="text-primary-600 size-5" />,
  },
  {
    label: "Don't Allow Notifications",
    description: "You won't receive any notifications about new opportunities.",
    value: 'deny',
    id: 'deny-notifications',
    icon: <BellOff className="size-5 text-gray-400" />,
  },
];

export function CommunicationTab({
  advisorState,
  errors,
  maxSchedulingLinkDisplayTextLength,
  handleAdvisorStateChange,
  handleShouldSendClientEmailChange,
  emailTags,
  handleEmailTagsChange,
  setHasUnsavedChanges,
  setAdvisorState,
  clearEmailWarnings,
  handleNotificationPreferenceChange,
}: CommunicationTabProps) {
  const currentPreference = advisorState.notificationPreference ?? 'allow';

  return (
    <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
      <AuthSeparator
        className="sm:col-span-6"
        text="Email Connection Settings"
      />
      <AdvisorField
        label="Connect Your Email Account (Beta)"
        description="Send personalized emails directly from your email account to strengthen client relationships and improve client engagement."
        tooltip="Connecting your email account lets Waterlily send important emails directly from your email address. This includes communications with clients about their plans, meeting details, and automated reminders. Only purely transactional emails will come from the Waterlily system."
      >
        <div className="flex flex-col gap-4 pt-2">
          <OAuthConnection provider="google" />
          <OAuthConnection provider="microsoft" />
        </div>
      </AdvisorField>

      <AuthSeparator
        className="sm:col-span-6"
        text="Notifications Preferences"
      />
      <AdvisorField
        label="Email Notification Preference"
        description="Would you like to receive email updates about new opportunities for your clients."
      >
        <RadioSelect
          options={notificationOptions}
          selectedValue={currentPreference}
          onChange={value => handleNotificationPreferenceChange(value as NotificationPreference)}
          className="tracking-normal"
          variant="box"
        />
      </AdvisorField>

      <AuthSeparator
        className="sm:col-span-6"
        text="Client Booking Settings"
      />
      <AuthInputTextField
        id="schedulingLinkUrl"
        label="Scheduling Link (allow clients to book time on your calendar)"
        value={advisorState.schedulingLinkUrl ?? ''}
        onChange={handleAdvisorStateChange('schedulingLinkUrl')}
        required={false}
        error={errors.schedulingLinkUrl}
        className="mt-0 sm:col-span-4"
      />
      {!errors.schedulingLinkUrl && (
        <TextButton
          buttonLabel="Open link to test"
          href={advisorState.schedulingLinkUrl}
          className="-mt-5 ml-1 text-sm sm:col-span-4"
        />
      )}
      <AuthInputTextField
        id="schedulingLinkDisplayText"
        label={`Scheduling Link Button Text (max ${maxSchedulingLinkDisplayTextLength} characters)`}
        value={advisorState.schedulingLinkDisplayText ?? ''}
        onChange={handleAdvisorStateChange('schedulingLinkDisplayText')}
        required={false}
        error={errors.schedulingLinkDisplayText}
        className="mt-0 sm:col-span-4"
        placeholder="Connect with an advisor"
      />

      <AuthSeparator
        className="sm:col-span-6"
        text="Client Results Sharing"
      />
      <AdvisorField label="Client Results Settings">
        <RadioSelect
          options={shouldSendClientEmailRadioOptions}
          selectedValue={advisorState.sendClientResultsLinkSetting ? 'yes' : 'no'}
          onChange={handleShouldSendClientEmailChange}
          className="tracking-normal"
          variant="box"
        />
      </AdvisorField>
      <AdvisorField
        label="Additional Recipients for Intake Forms"
        description="These emails will be CC'd on notifications for new intake form submissions."
      >
        <EmailTagInput
          onChange={emails => {
            handleEmailTagsChange(emails);
            setHasUnsavedChanges(true);
            setAdvisorState(prevState => ({
              ...prevState,
              additionalRecipientsForIntakeFormSubmissions: emails
                .filter(email => email.isValid)
                .map(email => email.value),
            }));
          }}
          initialEmails={emailTags}
          maxEmails={50}
          clearWarnings={clearEmailWarnings}
        />
      </AdvisorField>
    </div>
  );
}
